/**
 * Dates and the four-week cycle. Pure, total, and free of chore data — this is
 * the half of the old `src/planning.ts` that can be *wrong* rather than merely
 * ugly, which is why it is on its own and why it is tested.
 *
 * Everything here works in the device's local timezone on purpose. A checkmark
 * belongs to the day the household lived, not to a UTC day that starts at 02:00
 * in Brussels.
 */

/**
 * Monday 24 August 2026, the anchor of the four-week rotation. **Frozen.**
 *
 * Moving it re-labels which week of the cycle every past date belonged to, so
 * the month view starts showing checkmarks against chores that were never
 * displayed that day. Noon, not midnight, so a DST transition cannot tip the
 * date onto its neighbour.
 */
export const CYCLE_START = new Date(2026, 7, 24, 12, 0, 0)

/** `YYYY-MM-DD` in local time — the first component of every completion id. */
export function dateKey(date: Date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/

/**
 * The inverse of `dateKey`: a stored day back into a local date, or `null`.
 *
 * At noon, for the reason `monthDays` builds its dates at noon — `new
 * Date('2026-08-24')` is parsed as *UTC midnight* by the language, which in
 * Brussels is a date that renders as the 24th in summer and could render as the
 * 23rd elsewhere. Building the components explicitly keeps a day the household
 * lived a day in the household's own calendar.
 *
 * `null` rather than a throw, because what it is given comes off the wire and
 * out of `localStorage` — the same asymmetry `decodeCompletionId` makes.
 */
export function parseDateKey(value: string): Date | null {
  const match = ISO_DATE.exec(value)
  if (!match) return null

  const [, year, month, day] = match as unknown as [string, string, string, string]
  const date = new Date(Number(year), Number(month) - 1, Number(day), 12)

  // Rejects 2026-02-31, which the Date constructor would roll forward into
  // March rather than refuse.
  return dateKey(date) === value ? date : null
}

/**
 * Which of the four cycle weeks a date falls in, 0–3.
 *
 * Both dates are flattened to midnight before subtracting so that the division
 * counts whole days rather than hours, and the double modulo is what makes
 * dates *before* `CYCLE_START` work: `%` in JavaScript keeps the sign of the
 * dividend, so a plain `week % 4` returns -1 for the week before the anchor and
 * indexes off the end of the rotation.
 */
export function cycleWeekIndex(date: Date) {
  const start = new Date(
    CYCLE_START.getFullYear(),
    CYCLE_START.getMonth(),
    CYCLE_START.getDate(),
  )
  const current = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  const diff = Math.floor((current.getTime() - start.getTime()) / 86400000)
  const week = Math.floor(diff / 7)
  return ((week % 4) + 4) % 4
}

/**
 * Every day of a month, as local dates at noon.
 *
 * Noon again: a date built at midnight in a timezone that springs forward at
 * midnight lands on the previous day, which would silently shift a whole
 * month's checkmarks by one.
 */
export function monthDays(year: number, month: number) {
  const result: Date[] = []
  const last = new Date(year, month + 1, 0).getDate()
  for (let d = 1; d <= last; d++) result.push(new Date(year, month, d, 12))
  return result
}
